/**
 * Guards for the values these charts interpolate into an inline `<style>` tag.
 *
 * Every chart here builds a block of CSS custom properties from its
 * `ChartConfig` and injects it with `dangerouslySetInnerHTML`. That is the
 * upstream shadcn pattern and it validates nothing, so a colour string holding
 * `</style><script>` would break out of the tag.
 *
 * Nothing reaches that path from the API today, because chart colours are
 * written in our own source. The guard exists because QELM white-labels per
 * tenant: a later change that feeds a tenant-supplied colour into a chart
 * config would otherwise be a live cross-site-scripting hole with no gate in
 * front of it.
 *
 * A rejected value is DROPPED, never repaired. A half-escaped colour that still
 * renders would hide the mistake instead of showing it.
 */

/**
 * The characters a CSS colour value legitimately needs.
 *
 * This is a character allowlist rather than a grammar, because the colour
 * grammar keeps growing — `oklch()`, `color-mix()` and `var()` all have to
 * pass, and a per-function pattern goes stale. What matters is the escape
 * surface, and inside `<style>` within a `property: VALUE;` declaration that
 * surface is exactly `< > ; { } " ' \ :` — none of which appear below.
 */
const SAFE_COLOR_CHARS = /^[a-zA-Z0-9 #%.,()/_-]+$/;

/** Identifier characters CSS allows in a custom-property name or a selector value. */
const SAFE_IDENT = /^[a-zA-Z0-9_-]+$/;

/** The colour if it is safe to place after `:` in a declaration, else null. */
export function safeCssColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 128) return null;
  return SAFE_COLOR_CHARS.test(trimmed) ? trimmed : null;
}

/** The identifier if it is safe to place in a property name or selector, else null. */
export function safeCssIdent(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 64) return null;
  return SAFE_IDENT.test(trimmed) ? trimmed : null;
}
